import React, { useState } from 'react';
import { Calendar, CheckCircle, XCircle } from 'lucide-react';
import Button from '../../shared/Button';
import {
  useApproveEventDateChange,
  useRejectEventDateChange,
} from '../../../services/bookingDateChangeService';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const EventDateChangeReviewPanel = ({ bookingId, changeRequest, clientName, onResolved }) => {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState('');

  const approveMutation = useApproveEventDateChange({
    onSuccess: () => {
      setError('');
      onResolved?.();
    },
    onError: (err) => {
      setError(err?.response?.data?.message || 'Failed to approve request');
    },
  });

  const rejectMutation = useRejectEventDateChange({
    onSuccess: () => {
      setShowRejectForm(false);
      setRejectionReason('');
      setError('');
      onResolved?.();
    },
    onError: (err) => {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.rejection_reason?.[0] ||
          'Failed to reject request'
      );
    },
  });

  if (!changeRequest || changeRequest.status !== 'pending') {
    return null;
  }

  const handleApprove = () => {
    if (
      !window.confirm(
        `Approve new event date ${formatDate(changeRequest.requested_event_date)}? This will update the booking.`
      )
    ) {
      return;
    }
    approveMutation.mutate({ bookingId });
  };

  const handleReject = (e) => {
    e.preventDefault();
    if (rejectionReason.trim().length < 3) {
      setError('Please enter a reason (at least 3 characters).');
      return;
    }
    rejectMutation.mutate({ bookingId, rejection_reason: rejectionReason.trim() });
  };

  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
      <div className="flex items-start gap-2 mb-3">
        <Calendar className="w-5 h-5 text-amber-800 shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-bold text-amber-950">Event date change requested</h3>
          <p className="text-xs text-amber-900 mt-0.5">
            {clientName || 'Client'} asked to move this booking to a new date.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
        <div className="bg-white rounded-md p-3 border border-amber-200">
          <p className="text-xs text-gray-600">Current date</p>
          <p className="font-semibold text-black">
            {formatDate(changeRequest.previous_event_date)}
          </p>
        </div>
        <div className="bg-white rounded-md p-3 border border-amber-200">
          <p className="text-xs text-gray-600">Requested date</p>
          <p className="font-semibold text-black">
            {formatDate(changeRequest.requested_event_date)}
          </p>
        </div>
      </div>

      {changeRequest.client_note && (
        <div className="mb-4 text-sm">
          <p className="text-xs font-medium text-gray-600 mb-1">Client note</p>
          <p className="text-black bg-white rounded-md p-2 border border-amber-200">
            {changeRequest.client_note}
          </p>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3">
          {error}
        </p>
      )}

      {!showRejectForm ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            icon={CheckCircle}
            onClick={handleApprove}
            loading={approveMutation.isPending}
            disabled={rejectMutation.isPending}
            className="bg-green-700 hover:bg-green-800 text-white border-green-700"
          >
            Approve & update date
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            icon={XCircle}
            onClick={() => setShowRejectForm(true)}
            disabled={approveMutation.isPending}
            className="text-red-700 border-red-300 hover:bg-red-50"
          >
            Reject
          </Button>
        </div>
      ) : (
        <form onSubmit={handleReject} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Reason for rejection *
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Explain why this date cannot be accommodated..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-black"
              required
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              size="sm"
              loading={rejectMutation.isPending}
              disabled={approveMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white border-red-600"
            >
              Confirm rejection
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setShowRejectForm(false);
                setRejectionReason('');
                setError('');
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EventDateChangeReviewPanel;
